import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Divider, Typography, CircularProgress } from "@mui/material";
import { Stack } from "@mui/system";
import { useEffect, useState, useMemo } from "react";
import {
  Controller,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import { useNavigate, useParams, Navigate } from "react-router-dom";
import AppSelect from "../../../components/common/forms/AppSelect";
import AppTextField from "../../../components/common/forms/AppTextField";
import PageContainer from "../../../components/common/PageContainer";
import { appColors, appStyles } from "../../../theme";
import AccountActiveButton from "./AccountActiveButton";
import {
  accountsSchema,
  accountsFields,
  userAccountsInitialValues,
} from "./accounts-utils";
import {
  useCreateUser,
  useGetUserById,
  useUpdateUser,
} from "../../../hooks/useUser";
import LoadingIndicator from "../../../components/common/LoadingIndicator";
import { useSnackBar } from "../../../components/ToastProvider";
import { SelectOptionType } from "../../../components/common/forms/form-types";
import { Role } from "../Roles/roles-utils";
import { BusinessUnit } from "./businessUnit-utils";
import { useGetRolesQuery } from "../../../hooks/useRoles";
import { useGetBusinessUnitQuery } from "../../../hooks/useBusinessUnit";
import { usePermission } from "../../../hooks/usePermissions";
import { Modules, Permissions } from "../Roles/roles-utils";
//quins
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import OutlinedInput from "@mui/material/OutlinedInput";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ListSubheader from "@mui/material/ListSubheader";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import { useGetLocationsQuery } from "../../../hooks/useLocations";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: 320,
      width: 420,
    },
  },
  autoFocus: false,
  MenuListProps: {
    autoFocusItem: false,
  },
};

const AccountsForm = () => {
  const { id } = useParams();
  const hasAddPermission = usePermission(
    Modules.user.accessKey,
    Permissions.add,
  );
  const hasEditPermission = usePermission(
    Modules.user.accessKey,
    Permissions.edit,
  );
  const { showSnackBar } = useSnackBar();
  const roles = useGetRolesQuery();
  const businessUnit = useGetBusinessUnitQuery();
  const currentUser = useGetUserById();
  const updateUser = useUpdateUser();
  const createUser = useCreateUser();
  const navigate = useNavigate();
  const {
    register,
    control,
    handleSubmit,
    setValue,
    reset,
    formState: { errors, isDirty, isValid },
  } = useForm<accountsFields>({
    defaultValues: currentUser.data
      ? currentUser.data
      : userAccountsInitialValues,
    resolver: yupResolver(accountsSchema),
    mode: "onChange",
  });

  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [initialLocations, setInitialLocations] = useState<string[]>([]);
  const locationsQuery = useGetLocationsQuery();
  const isEditMode = Boolean(currentUser.data);

  useEffect(() => {
    if (currentUser.data && locationsQuery.data) {
      const userLocs = locationsQuery.data
        .filter((loc) => loc.userIds?.includes(currentUser.data.id))
        .map((loc) => loc.id);
      setSelectedLocations(userLocs);
      setInitialLocations(userLocs);
    }
  }, [currentUser.data, locationsQuery.data]);

  const isLocationsDirty = useMemo(() => {
    if (selectedLocations.length !== initialLocations.length) return true;
    const sortedSelected = [...selectedLocations].sort();
    const sortedInitial = [...initialLocations].sort();
    return sortedSelected.some(
      (locId, index) => locId !== sortedInitial[index],
    );
  }, [selectedLocations, initialLocations]);

  const isFormDirty = isDirty || isLocationsDirty;

  const candidateLocations = useMemo(() => {
    return locationsQuery.data || [];
  }, [locationsQuery.data]);

  const [searchLocation, setSearchLocation] = useState("");

  const filteredLocations = useMemo(() => {
    const q = searchLocation.trim().toLowerCase();
    if (!q) return candidateLocations;
    return candidateLocations.filter((loc) => {
      const name = (loc.name || "").toLowerCase();
      const type = (loc.type || "").toLowerCase();
      const status = loc.isActive ? "active" : "inactive";
      return name.includes(q) || type.includes(q) || status.includes(q);
    });
  }, [candidateLocations, searchLocation]);

  const filteredLocationIds = useMemo(
    () => filteredLocations.map((loc) => loc.id),
    [filteredLocations],
  );

  const isAllSelected =
    filteredLocationIds.length > 0 &&
    filteredLocationIds.every((id) => selectedLocations.includes(id));

  const isIndeterminate =
    !isAllSelected &&
    filteredLocationIds.some((id) => selectedLocations.includes(id));

  const handleLocationsChange = (newSelected: string[]) => {
    setSelectedLocations(newSelected);
  };

  const handleSelectChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const selectedArray = typeof value === "string" ? value.split(",") : value;

    if (selectedArray.includes("all")) {
      if (isAllSelected) {
        const updated = selectedLocations.filter(
          (id) => !filteredLocationIds.includes(id),
        );
        handleLocationsChange(updated);
      } else {
        const set = new Set([...selectedLocations, ...filteredLocationIds]);
        handleLocationsChange(Array.from(set));
      }
      return;
    }

    handleLocationsChange(selectedArray);
  };

  const handleRemoveLocation = (locId: string) => {
    const updated = selectedLocations.filter((id) => id !== locId);
    handleLocationsChange(updated);
  };

  const superadmin =
    currentUser?.data?.roleId! === "ROLE::81a4a958-2a28-4a77-ab3c-6e4622793baf";
  const rolesData: Role[] = roles?.data === undefined ? [] : roles.data;
  const businessUnitData: BusinessUnit[] =
    businessUnit?.data === undefined ? [] : businessUnit.data;

  const roleOptions: SelectOptionType[] = rolesData
    .filter((e) =>
      superadmin ? e : e.id !== "ROLE::81a4a958-2a28-4a77-ab3c-6e4622793baf",
    )
    .map((e) => ({
      id: e.id,
      label: e.name,
      value: e.id,
    }));

  const businessUnitOptions: SelectOptionType[] = businessUnitData.map((e) => ({
    id: e.id,
    label: e.name,
    value: e.id,
  }));

  useEffect(() => {
    if (currentUser.data) {
      reset({
        firstName: currentUser.data.firstName || "",
        lastName: currentUser.data.lastName || "",
        contactNumber: currentUser.data.contactNumber || "",
        email: currentUser.data.email || "",
        username: currentUser.data.username || "",
        isActive: currentUser.data.isActive ?? true,
        roleName: currentUser.data.roleName || "",
        roleId: currentUser.data.roleId || "",
        businessUnitId: currentUser.data.businessUnitId || "",
      });
    }
  }, [currentUser.data, reset]);

  useEffect(() => {
    const handleError = (error: unknown) => {
      const errData = (error as { response?: { data?: { error?: unknown } } })
        ?.response?.data?.error;

      const errMsg =
        typeof errData === "string"
          ? errData
          : Array.isArray(errData)
            ? typeof errData[0] === "string"
              ? errData[0]
              : errData[0]?.message || "Network Error"
            : "Network Error";

      showSnackBar(errMsg, "error");
    };

    if (createUser?.isError) {
      handleError(createUser.error);
    }

    if (updateUser?.isError) {
      handleError(updateUser.error);
    }
  }, [
    createUser?.isError,
    createUser?.error,
    updateUser?.isError,
    updateUser?.error,
  ]);

  if (!hasAddPermission && !hasEditPermission) return <Navigate to="/home" />;

  const onError: SubmitErrorHandler<accountsFields> = (err) => {
    console.error(err);
  };

  const onSubmit: SubmitHandler<accountsFields> = (userAccount) => {
    const payload = { ...userAccount, locationIds: selectedLocations };
    if (currentUser.data) {
      updateUser.mutate(payload);
    }
    if (!currentUser.data) {
      createUser.mutate(payload);
    }
  };

  const backTo = () => navigate("/users");

  const isSubmitting = createUser.isLoading || updateUser.isLoading;

  if (id !== undefined && currentUser.isLoading && !currentUser.data) {
    return <LoadingIndicator show />;
  }
  if (
    (roles.isLoading && !roles.data) ||
    (businessUnit.isLoading && !businessUnit.data)
  ) {
    return <LoadingIndicator show />;
  }

  return (
    <>
      <PageContainer
        title="User Management"
        subTitle="User"
        controls={
          <Stack direction="row" gap={2}>
            <Button
              sx={{ backgroundColor: appColors.lightGray, color: "black" }}
              onClick={backTo}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit(onSubmit, onError)}
              disabled={
                !isValid ||
                !isFormDirty ||
                isSubmitting ||
                (isEditMode ? !hasEditPermission : !hasAddPermission)
              }
              disableElevation
              variant="contained"
              sx={{
                textTransform: "capitalize",
                borderRadius: 1.5,
                minWidth: 80,
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Save"
              )}
            </Button>
          </Stack>
        }
      >
        <Divider color="black" />
        <Stack
          direction="column"
          gap={6}
          component="form"
          height="100%"
          bgcolor="white"
          py={3}
          boxShadow={appStyles.shadow}
          borderRadius={1}
        >
          <Stack
            direction="row"
            gap={6}
            px={3}
            height="100%"
            overflow="auto"
            justifyContent="flex-start"
            alignItems="flex-start"
          >
            <Stack direction="column" gap={3} width={550}>
              <AppTextField
                required
                label="First Name"
                placeholder="First Name"
                {...register("firstName")}
                error={!!errors.firstName}
                helperText={errors.firstName?.message}
              />
              <AppTextField
                required
                label="Last Name"
                placeholder="Last Name"
                {...register("lastName")}
                error={!!errors.lastName}
                helperText={errors.lastName?.message}
              />

              <AppTextField
                required
                label="Email"
                placeholder="Email"
                {...register("email")}
                error={!!errors.email}
                helperText={errors.email?.message}
              />
              <AppTextField
                label="Username"
                placeholder="Username"
                {...register("username")}
                error={!!errors.username}
                helperText={errors.username?.message}
              />
              <AppTextField
                required
                label="Contact No."
                type="number"
                maxLength="11"
                pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
                placeholder="Contact Number"
                {...register("contactNumber")}
                error={!!errors.contactNumber}
                helperText={errors.contactNumber?.message}
              />

              {currentUser.data ? (
                <Controller
                  control={control}
                  name="isActive"
                  render={({ field: { onChange, value } }) => (
                    <AccountActiveButton
                      value={value}
                      onChange={(e, v) => onChange(v)}
                    />
                  )}
                />
              ) : null}

              <Controller
                control={control}
                name="roleId"
                render={({ field }) => (
                  <AppSelect
                    label={
                      <>
                        Role{" "}
                        <Typography component={"span"} color="error.main">
                          *
                        </Typography>
                      </>
                    }
                    placeholder="Role"
                    error={!!errors.roleId}
                    helperText={errors.roleId?.message}
                    options={roleOptions}
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="businessUnitId"
                render={({ field }) => (
                  <AppSelect
                    label={
                      <>
                        Business Unit{" "}
                        <Typography component={"span"} color="error.main">
                          *
                        </Typography>
                      </>
                    }
                    placeholder="Business Unit"
                    error={!!errors.businessUnitId}
                    helperText={errors.businessUnitId?.message}
                    options={businessUnitOptions}
                    {...field}
                  />
                )}
              />
            </Stack>
            <Stack direction="column" gap={1.5} width={400}>
              <Box
                component="label"
                htmlFor="assigned-projects-select"
                sx={{
                  position: "relative",
                  left: -4,
                  fontSize: "15px",
                  fontWeight: 600,
                  color: appColors.formText,
                }}
              >
                Assigned Projects
              </Box>
              <FormControl fullWidth size="small">
                <Select
                  id="assigned-projects-select"
                  multiple
                  displayEmpty
                  value={selectedLocations}
                  onChange={handleSelectChange}
                  onClose={() => setSearchLocation("")}
                  input={<OutlinedInput />}
                  renderValue={(selected) => {
                    const selectedIds = selected as string[];
                    if (selectedIds.length === 0) {
                      return (
                        <Typography variant="body2" color="text.secondary">
                          Select projects for User
                        </Typography>
                      );
                    }
                    return selectedIds
                      .map((lid) => {
                        const l = locationsQuery.data?.find(
                          (loc) => loc.id === lid,
                        );
                        return l ? l.name : "Project";
                      })
                      .join(", ");
                  }}
                  MenuProps={MenuProps}
                  sx={{
                    borderRadius: 1,
                    backgroundColor: "#fff",
                    height: 40,
                  }}
                >
                  <ListSubheader
                    disableSticky={false}
                    sx={{
                      position: "sticky",
                      top: 0,
                      bgcolor: "#fff",
                      zIndex: 3,
                      p: "6px 8px",
                      lineHeight: "normal",
                      borderBottom: "1px solid #e0e0e0",
                    }}
                    onKeyDown={(e) => {
                      if (e.key !== "Escape") {
                        e.stopPropagation();
                      }
                    }}
                  >
                    <TextField
                      size="small"
                      autoFocus
                      placeholder="Search project..."
                      fullWidth
                      value={searchLocation}
                      onChange={(e) => setSearchLocation(e.target.value)}
                      onClick={(e) => e.stopPropagation()}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon
                              fontSize="small"
                              sx={{ color: "text.secondary" }}
                            />
                          </InputAdornment>
                        ),
                        endAdornment: searchLocation ? (
                          <InputAdornment position="end">
                            <IconButton
                              size="small"
                              edge="end"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSearchLocation("");
                              }}
                            >
                              <ClearIcon fontSize="small" />
                            </IconButton>
                          </InputAdornment>
                        ) : null,
                        sx: { fontSize: "13.5px" },
                      }}
                    />
                  </ListSubheader>
                  {filteredLocations.length === 0 && (
                    <MenuItem
                      disabled
                      sx={{ justifyContent: "center", py: 1.5 }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No projects found
                      </Typography>
                    </MenuItem>
                  )}
                  {filteredLocations.length > 0 && (
                    <MenuItem
                      value="all"
                      sx={{
                        position: "sticky",
                        top: 45,
                        bgcolor: "#fff",
                        zIndex: 2,
                        borderBottom: "1px solid #e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        py: 0.5,
                        gap: 0.5,
                        "&:hover": { bgcolor: "#f5f5f5" },
                      }}
                    >
                      <Checkbox
                        checked={isAllSelected}
                        indeterminate={isIndeterminate}
                        sx={{ p: 0.5 }}
                      />
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {isAllSelected ? "Unselect All" : "Select All"}
                      </Typography>
                    </MenuItem>
                  )}
                  {filteredLocations.map((loc) => {
                    const isSelected = selectedLocations.includes(loc.id);
                    return (
                      <MenuItem
                        key={loc.id}
                        value={loc.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          py: 0.5,
                          gap: 0.5,
                        }}
                      >
                        <Checkbox checked={isSelected} sx={{ p: 0.5 }} />
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            minWidth: 0,
                            flex: 1,
                            gap: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            noWrap
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              fontWeight: isSelected ? 600 : 400,
                              flexShrink: 1,
                            }}
                          >
                            {loc.name}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: "nowrap",
                              flexShrink: 0,
                              color: "text.secondary",
                              fontSize: "12.5px",
                            }}
                          >
                            ({loc.type}){" "}
                            <Box
                              component="span"
                              sx={{
                                color: loc.isActive
                                  ? "success.main"
                                  : "error.main",
                                fontWeight: 500,
                              }}
                            >
                              {loc.isActive ? "" : "Inactive"}
                            </Box>
                          </Typography>
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
              <Box
                sx={{
                  width: 400,
                  height: 335,
                  overflowY: "auto",
                  border: "1px solid #dcdcdc",
                  borderRadius: 1,
                  p: 1.5,
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1,
                  alignContent: "flex-start",
                  bgcolor: "#fafafa",
                }}
              >
                {selectedLocations.length === 0 ? (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ m: "auto" }}
                  >
                    No projects assigned to this user
                  </Typography>
                ) : (
                  selectedLocations.map((locId) => {
                    const loc = locationsQuery.data?.find(
                      (l) => l.id === locId,
                    );
                    const isActive = loc ? loc.isActive : true;
                    const displayName = loc ? (
                      <span>
                        {loc.name}
                        {!isActive && (
                          <span style={{ color: "#d32f2f", fontWeight: 600 }}>
                            {" "}
                            (Inactive)
                          </span>
                        )}
                      </span>
                    ) : (
                      locId
                    );
                    return (
                      <Chip
                        key={locId}
                        label={displayName}
                        color={isActive ? "primary" : "default"}
                        variant={isActive ? "outlined" : "filled"}
                        onDelete={() => handleRemoveLocation(locId)}
                        sx={
                          !isActive
                            ? {
                                backgroundColor: "#e0e0e0",
                                color: "#374151",
                                fontWeight: 500,
                                border: "1px solid #d1d5db",
                                "& .MuiChip-deleteIcon": {
                                  color: "#4b5563",
                                  "&:hover": { color: "#111827" },
                                },
                              }
                            : undefined
                        }
                      />
                    );
                  })
                )}
              </Box>
            </Stack>
          </Stack>
        </Stack>
      </PageContainer>
    </>
  );
};

export default AccountsForm;
